<script setup lang="ts">
import { computed, ref } from 'vue';
import type { VNode } from 'vue';
import { useAuthStore } from '@/store/modules/auth';
import { useRouterPush } from '@/hooks/common/router';
import { useSvgIcon } from '@/hooks/common/icon';
import { $t } from '@/locales';
import { localStg } from '@/utils/storage';
import { ossUrl } from '@/constants/apiConfig';
import UpdateLang from '@/components/common/update-lang.vue';

defineOptions({
  name: 'UserAvatar'
});

const { systemInfo, loginOut, getUserInfo } = useAuthStore();
const { routerPushByKey, toLogin } = useRouterPush();
const { SvgIconVNode } = useSvgIcon();

function loginOrRegister() {
  toLogin();
}

type DropdownKey = 'user_center' | 'logout' | 'user_company' | 'user_lang';

type DropdownOption =
  | {
      key: DropdownKey;
      label: string;
      icon?: () => VNode;
    }
  | {
      type: 'divider';
      key: string;
    };

const options = computed(() => {
  const opts: DropdownOption[] = [
    {
      label: $t('common.userCenter'),
      key: 'user_center',
      icon: SvgIconVNode({
        localIcon: 'material-symbols-person',
        fontSize: 18,
        color: '#5E63E4'
      })
    },
    {
      type: 'divider',
      key: 'divider'
    }
  ];

  if (systemInfo.tenantId !== '1') {
    opts.push(
      {
        label: $t('common.companyMsg'),
        key: 'user_company',
        icon: SvgIconVNode({
          localIcon: 'ph-buildings',
          fontSize: 18,
          color: '#5E63E4'
        })
      },
      {
        type: 'divider',
        key: 'divider'
      }
    );
  }

  if (getUserInfo()?.roleId == '3'||getUserInfo()?.roleId == '1'||getUserInfo()?.roleId == '2') {
    opts.push(
      {
        label: $t('common.langManage'),
        key: 'user_lang',
        icon: SvgIconVNode({
          localIcon: 'language-manager',
          fontSize: 18,
          color: '#5E63E4'
        })
      },
      {
        type: 'divider',
        key: 'divider'
      }
    );
  }

  opts.push({
    label: $t('common.logout'),
    key: 'logout',
    icon: SvgIconVNode({
      localIcon: 'log-out',
      fontSize: 18,
      color: '#5E63E4'
    })
  });

  return opts;
});

function logout() {
  window.$dialog?.info({
    title: $t('common.tip'),
    content: $t('common.logoutConfirm'),
    positiveText: $t('common.confirm'),
    negativeText: $t('common.cancel'),
    onPositiveClick: () => {
      loginOut();
    }
  });
}

const updateLangRef = ref<InstanceType<typeof UpdateLang>>();
const updateLang = () => {
  updateLangRef.value?.openModel();
};

function handleDropdown(key: DropdownKey) {
  if (key === 'logout') {
    logout();
  } else if (key === 'user_lang') {
    updateLang();
  } else {
    routerPushByKey(key);
  }
}

const isLogin = Boolean(localStg.get('token'));
</script>

<template>
  <NButton v-if="!isLogin" quaternary @click="loginOrRegister">
    {{ $t('page.login.common.loginOrRegister') }}
  </NButton>
  <NDropdown v-else placement="bottom" :options="options" @select="handleDropdown">
    <div>
      <NButton type="primary" class="text-16px text-white font-medium">
        <SvgIcon v-if="!systemInfo.avatar" icon="ph:user-circle" class="text-icon-large text-white" />
        <NAvatar v-else size="small" :src="ossUrl + systemInfo.avatar" />
        <span class="m-2 text-16px text-white font-medium">
          {{ systemInfo.nickname ? systemInfo.nickname : systemInfo.email }}
        </span>
        <icon-local-material-symbols-keyboard-arrow-down class="text-icon" />
      </NButton>
    </div>
  </NDropdown>
  <UpdateLang ref="updateLangRef" />
</template>

<style scoped></style>
